import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  reportAdminService,
  ReportRow,
  ReportStatus,
  ReportActionTaken,
} from '../services/reportAdminService';

// FEAT-041 — Side panel that opens on a row click in the moderation queue.
// Renders the full report context (reporter, target snapshot, reason,
// free-text context) and the transition controls. Each transition writes
// an audit row server-side (resolvedById + resolvedAt + resolutionNotes).

const STATUS_OPTIONS: ReportStatus[] = [
  'OPEN',
  'UNDER_REVIEW',
  'RESOLVED',
  'DISMISSED',
];

const ACTION_OPTIONS: ReportActionTaken[] = [
  'NONE',
  'WARNED',
  'RESTRICTED',
  'SUSPENDED',
];

export interface ReportReviewSidebarProps {
  reportId: string | null;
  open: boolean;
  onClose: () => void;
  onUpdated?: (report: ReportRow) => void;
}

const formatSnapshot = (report: ReportRow): { title: string; details: string[] } => {
  if (report.targetType === 'PROFILE') {
    const snap = report.targetSnapshot as {
      reporteeId?: string;
      displayName?: string | null;
      role?: string;
    };
    return {
      title: snap?.displayName || 'Reported profile',
      details: [
        `User ID: ${snap?.reporteeId ?? report.targetId}`,
        snap?.role ? `Role: ${snap.role}` : '',
      ].filter(Boolean),
    };
  }
  const snap = report.targetSnapshot as {
    conversationId?: string;
    senderId?: string;
    excerpt?: string;
    sentAt?: string;
  };
  return {
    title: 'Reported message',
    details: [
      `Sender ID: ${snap?.senderId ?? 'unknown'}`,
      snap?.sentAt ? `Sent: ${new Date(snap.sentAt).toLocaleString()}` : '',
      snap?.excerpt ? `Excerpt: ${snap.excerpt}` : '',
    ].filter(Boolean),
  };
};

const ReportReviewSidebar: React.FC<ReportReviewSidebarProps> = ({
  reportId,
  open,
  onClose,
  onUpdated,
}) => {
  const [report, setReport] = useState<ReportRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ReportStatus>('OPEN');
  const [actionTaken, setActionTaken] = useState<ReportActionTaken>('NONE');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !reportId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportAdminService
      .getReport(reportId)
      .then(({ report: r }) => {
        if (cancelled) return;
        setReport(r);
        setStatus(r.status);
        setActionTaken(r.actionTaken);
        setResolutionNotes(r.resolutionNotes ?? '');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load report.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, reportId]);

  const handleSave = async () => {
    if (!report) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await reportAdminService.transitionReport(report.id, {
        status,
        actionTaken,
        resolutionNotes: resolutionNotes || undefined,
      });
      setReport(result.report);
      onUpdated?.(result.report);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update report.');
    } finally {
      setSubmitting(false);
    }
  };

  const snapshot = report ? formatSnapshot(report) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={submitting ? undefined : onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Report review</Typography>
          <IconButton onClick={onClose} aria-label="Close report review" disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" data-testid="report-review-error">
              {error}
            </Alert>
          ) : report && snapshot ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reported entity
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {snapshot.title}
                </Typography>
                {snapshot.details.map((line) => (
                  <Typography
                    key={line}
                    variant="body2"
                    color="text.secondary"
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reporter
                </Typography>
                <Typography variant="body1">
                  {report.reporter?.firstName} {report.reporter?.lastName} (
                  {report.reporter?.email})
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reason
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Chip label={report.reasonCode} size="small" />
                  <Chip label={report.targetType} size="small" variant="outlined" />
                </Stack>
              </Box>

              {report.contextText ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Context from reporter
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                    data-testid="report-review-context"
                  >
                    {report.contextText}
                  </Typography>
                </Box>
              ) : null}

              <Divider />

              <FormControl size="small" fullWidth>
                <InputLabel id="report-status-label">Status</InputLabel>
                <Select
                  labelId="report-status-label"
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ReportStatus)}
                  data-testid="report-status-select"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="report-action-label">Action taken</InputLabel>
                <Select
                  labelId="report-action-label"
                  label="Action taken"
                  value={actionTaken}
                  onChange={(e) => setActionTaken(e.target.value as ReportActionTaken)}
                  data-testid="report-action-select"
                >
                  {ACTION_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Resolution notes (audit trail)"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                inputProps={{ 'data-testid': 'report-resolution-notes' }}
              />

              {report.resolvedBy ? (
                <Typography variant="caption" color="text.secondary">
                  Last resolved by {report.resolvedBy.email}
                  {report.resolvedAt
                    ? ` on ${new Date(report.resolvedAt).toLocaleString()}`
                    : ''}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a report to review.
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={submitting || !report}
            data-testid="report-review-save"
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ReportReviewSidebar;
